import React, { useEffect, useState, useMemo } from "react";
import { cn } from "@/utils/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ChevronDown } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import IconContainer from "@/components/ui/icon-container";
import { isEmpty, keys, map } from "lodash";
import SingleSelect from "@/components/controls/SingleSelect";

export interface Props {
  label: string;
  placeholder?: string;
  selected?: boolean;
  counter?: number;
  onDropdownValueSelected?: (value: string) => void;
  options?: {
    [k: string]: string;
  };
  defaultDropdownValue?: string;
  onArrowClick?: () => void;
  shouldRenderArrow?: boolean;
  isAsc?: boolean;
  onTriggerClick?: () => void;
  customIcon?: JSX.Element;
  className?: string;
}

const FilterButton: React.FC<Props> = ({
  label,
  placeholder,
  selected,
  counter,
  onDropdownValueSelected,
  options,
  defaultDropdownValue,
  onArrowClick,
  shouldRenderArrow = true,
  isAsc = true,
  onTriggerClick,
  customIcon,
  className
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultDropdownValue);

  useEffect(() => {
    setSelectedValue(defaultDropdownValue);
  }, [defaultDropdownValue]);

  const handleSelect = (currentValue: string | number) => {
    if (onDropdownValueSelected) {
      onDropdownValueSelected(String(currentValue));
    }
    setSelectedValue(String(currentValue));
    setIsMenuOpen(false);
  };

  const dropdownOptions = useMemo(() => {
    if (!options) return [];
    return map(keys(options), (key) => ({
      id: key,
      displayName: options[key]
    }));
  }, [options]);

  const ButtonComponent = (
    <Button
      variant="secondaryNoBg"
      className={cn(
        "gap-1 px-[6px] py-1 border-transparent group cursor-pointer rounded-[6px]",
        className
      )}
      selected={isMenuOpen || selected}
      data-state={isMenuOpen ? "open" : "closed"}
      onClick={onTriggerClick}
    >
      {shouldRenderArrow && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="p-[2px] h-fit rounded font-normal text-secondary hover:bg-surface-hover cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onArrowClick?.();
              }}
            >
              <IconContainer size="5" nativeStroke>
                {isAsc ? <ArrowUp /> : <ArrowDown />}
              </IconContainer>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {t(
              `datatoolbar.filters.sortByDirections.${isAsc ? "asc" : "desc"}`
            )}
          </TooltipContent>
        </Tooltip>
      )}
      {customIcon && customIcon}
      <div className="flex items-center gap-[6px] cursor-pointer">
        {label && (
          <Text
            as="label"
            variant="bodySecondary"
            className="text-nowrap cursor-pointer"
          >
            {label}
          </Text>
        )}
        {counter && (
          <Badge
            text={counter.toString()}
            className="py-[1px] px-[2px] min-w-6 rounded-sm"
            textClassName="text-secondary"
          />
        )}
        {placeholder && isEmpty(selectedValue) && (
          <Text as="span" variant="bodySecondary" className="cursor-pointer">
            {placeholder}
          </Text>
        )}
        {selectedValue && options && (
          <Text
            as="span"
            variant={placeholder ? "body" : "bodySecondary"}
            className="min-w-fit text-nowrap cursor-pointer"
          >
            {options[selectedValue]}
          </Text>
        )}
      </div>
      {options && (
        <IconContainer
          size="5"
          color="secondary"
          className="transition-transform duration-200 group-data-[state=open]:rotate-180 group-data-[state=closed]:rotate-0"
          data-state={isMenuOpen ? "open" : "closed"}
        >
          <ChevronDown />
        </IconContainer>
      )}
    </Button>
  );

  return options ? (
    <div className="inline-flex items-center rounded-md border-[0.5px] border-border-badge-default bg-badges-default">
      {/* Label Section */}
      <div className="h-7 px-[6px] flex items-center border-r-[0.5px] border-border-badge-default">
        <Text variant="body" className="text-secondary text-nowrap">
          {label}
        </Text>
      </div>

      {/* Value Section with Dropdown */}
      <div className="flex items-center flex-1 h-7 px-[6px] hover:bg-surface-hover-strong transition-colors duration-200 cursor-pointer border-r-[0.5px] border-border-badge-default [&_svg.lucide-chevron-down]:hidden">
        <SingleSelect
          fieldName="sort-filter"
          options={dropdownOptions}
          selectedValue={selectedValue || ""}
          onSelection={handleSelect}
          placeholder={placeholder}
          showSearch={false}
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          className="!p-0 !border-0 !rounded-none !bg-transparent hover:!bg-transparent !h-auto"
          popoverTriggerClassName="!p-0 !h-auto"
          popoverClassName="w-40 min-w-0 max-h-[32vh]"
          triggerVariant="body"
        />
      </div>


      {/* Sort Arrow - Optional */}
      {shouldRenderArrow && (
        <div className="flex justify-center items-center h-7 px-[6px] hover:bg-surface-hover-strong cursor-pointer transition-colors duration-200">
          <Tooltip>
            <TooltipTrigger asChild>
              <IconContainer
                size="5"
                color="primary"
                nativeStroke
                onClick={(e) => {
                  e.stopPropagation();
                  onArrowClick?.();
                }}
                className="[&_svg]:size-5"
              >
                {isAsc ? <ArrowUp /> : <ArrowDown />}
              </IconContainer>
            </TooltipTrigger>
            <TooltipContent>
              {t(
                `datatoolbar.filters.sortByDirections.${isAsc ? "asc" : "desc"}`
              )}
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  ) : (
    ButtonComponent
  );
};

export default FilterButton;
